import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useProgression } from '../auth/ProgressionProvider';
import { useSeason } from '../auth/SeasonProvider';
import { listBuiltinPulseTracks } from '@shared/builtinPulseMaps';
import { chartDurationSeconds, formatDuration, stripVariantSuffix } from '@shared/chart';
import { VARIANTS, type Variant, variantLabel } from '@shared/gameTypes';
import { isTutorialUnseen } from '../game/tutorialFlag';
import { saveModePref } from '../game/modePref';
import { modePath } from '../game/modeRouting';
import { TrophyStrip } from '../components/TrophyStrip';
import { ModeSeoSection } from './ModeSeoSection';
import '../components/trophyStrip.css';
import './home.css';

const VARIANT_STORAGE_KEY = 'gravitydash:home-variant:v1';

/**
 * Recover the last-selected variant from localStorage so a returning player
 * lands back on Hard/Extreme without having to re-pick. Falls back to
 * Normal on any storage error or unrecognised value.
 */
function loadStoredVariant(): Variant {
  try {
    const raw = localStorage.getItem(VARIANT_STORAGE_KEY);
    if (raw && (VARIANTS as ReadonlyArray<string>).includes(raw)) {
      return raw as Variant;
    }
  } catch (err) {
    // Storage might be disabled (Safari private mode etc.). Default below.
    console.warn('Failed to read variant from localStorage:', err);
  }
  return 'normal';
}

function persistVariant(variant: Variant): void {
  try {
    localStorage.setItem(VARIANT_STORAGE_KEY, variant);
  } catch (err) {
    console.warn('Failed to persist variant to localStorage:', err);
  }
}

/**
 * Per-variant blurb shown under the difficulty selector. Sets expectations
 * for what the player is opting into without leaving the home page.
 */
const VARIANT_DESCRIPTIONS: Record<Variant, string> = {
  normal: 'The original chart. Good place to learn the song.',
  hard: 'Denser pad layout — weaker hits and tighter clusters become tappable.',
  extreme: 'Maximum density. Fast clusters, ghost kicks, full-throttle pace.',
};

export function HomePage() {
  const { forMap } = useProgression();
  const { crownsForMap } = useSeason();
  const tracks = listBuiltinPulseTracks();
  const navigate = useNavigate();
  const [variant, setVariant] = useState<Variant>(() => loadStoredVariant());

  useEffect(() => {
    // Landing here means the player picked Pulse from the mode picker
    // (or arrived via a legacy /play redirect). Persist that choice so
    // a later visit to /profile or /calibrate keeps surfacing Pulse
    // links in the TopNav rather than reverting to the saved default.
    saveModePref('pulse');
  }, []);

  useEffect(() => {
    // First-time Pulse players get diverted into the tutorial. The
    // route-level AuthGate guarantees we only mount after auth is
    // ready, so we can read the per-mode tutorial flag immediately.
    if (isTutorialUnseen('pulse')) {
      navigate(modePath('pulse', 'tutorial'), { replace: true });
    }
  }, [navigate]);

  /**
   * Mirror the selected variant across browser tabs. Without this, a player
   * with two tabs open would see them silently disagree about which density
   * they're browsing. We re-read storage rather than trusting `newValue`
   * directly so a `removeItem` / `clear()` in another tab correctly resets
   * us back to the default variant instead of being silently ignored.
   *
   * The `null` event.key signals a `localStorage.clear()` — handle that too.
   */
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== VARIANT_STORAGE_KEY && event.key !== null) {
        return;
      }
      setVariant(loadStoredVariant());
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleVariantChange = (next: Variant) => {
    setVariant(next);
    persistVariant(next);
  };

  return (
    <div className="home">
      <header className="home-hero">
        <h1 className="home-hero-kicker">
          BEAT<span style={{ color: 'var(--accent-cyan)' }}>PULSE</span>
        </h1>
        <div className="home-cta">
          <Link to={modePath('pulse', 'play/on-and-on')} className="btn btn-primary btn-large" data-testid="play-cta">
            Play On &amp; On
          </Link>
          <Link to={modePath('pulse', 'tutorial')} className="btn">
            How to play
          </Link>
          <Link to={modePath('pulse', 'browse')} className="btn">
            Community maps
          </Link>
          <Link to="/calibrate" className="btn btn-ghost">
            Calibrate audio
          </Link>
        </div>
      </header>

      <section className="home-maps">
        <div className="home-maps-header">
          <h2>Tracks</h2>
          <p className="muted faint">
            Built-in maps in order of difficulty. Scroll for more.
          </p>
        </div>

        <div
          className="variant-selector"
          role="group"
          aria-label="Pad density"
          data-testid="variant-selector"
        >
          {VARIANTS.map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={variant === v}
              className={`variant-tab variant-tab-${v} ${variant === v ? 'is-active' : ''}`}
              onClick={() => handleVariantChange(v)}
              data-testid={`variant-tab-${v}`}
            >
              {variantLabel(v)}
            </button>
          ))}
          <p className="variant-hint muted" aria-live="polite">
            {VARIANT_DESCRIPTIONS[variant]}
          </p>
        </div>

        <ol className="map-list">
          {tracks.map((track, idx) => {
            const chart = track.variants[variant].chart;
            const padCount = chart.pads.filter((p) => !p.optional).length;
            const trophies = forMap(chart.slug, 'pulse');
            return (
              <li key={track.baseSlug} className="map-list-item">
                <Link
                  to={modePath('pulse', `play/${chart.slug}`)}
                  className="map-row"
                  data-testid={`map-card-${track.baseSlug}`}
                  style={
                    {
                      '--map-bg': chart.palette.bg,
                      '--map-bg-accent': chart.palette.bgAccent,
                      '--map-pad': chart.palette.pad,
                      '--map-player': chart.palette.player,
                    } as React.CSSProperties
                  }
                >
                  <div className="map-row-index">{String(idx + 1).padStart(2, '0')}</div>
                  <div className="map-row-preview">
                    <div className="map-row-rail map-row-rail-top" />
                    <div className="map-row-rail map-row-rail-bottom" />
                    <div className="map-row-player" />
                  </div>
                  <div className="map-row-body">
                    <div className="map-row-title">
                      {/*
                        The chart's `title` already includes the variant
                        suffix for hard/extreme (`On & On — Hard`). On the
                        homepage we strip it back to the bare song name —
                        the variant-tab strip above already tells the
                        player which density they're browsing, so repeating
                        it on every row is noisy.
                      */}
                      {stripVariantSuffix(chart)}
                    </div>
                    <div className="map-row-desc">{chart.description}</div>
                  </div>
                  <div className="map-row-meta">
                    <div className="map-row-bpm">{chart.bpm.toFixed(0)} BPM</div>
                    <div className="map-row-duration" title="Duration">
                      {formatDuration(chartDurationSeconds(chart.bpm, chart.totalBeats))}
                    </div>
                    <div
                      className="map-row-pad-count"
                      title={`${padCount} required pads on ${variantLabel(variant)}`}
                    >
                      {padCount} pads
                    </div>
                    <div className="map-row-difficulty" aria-label={`Difficulty ${chart.difficulty}`}>
                      {Array.from({ length: 5 }, (_, i) => (
                        <span
                          key={i}
                          className={i < chart.difficulty ? 'pip pip-on' : 'pip'}
                        />
                      ))}
                    </div>
                    <TrophyStrip
                      stars={trophies.stars}
                      cups={trophies.cups}
                      crowns={crownsForMap(chart.slug, 'pulse')}
                      size="sm"
                    />
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      <ModeSeoSection
        mode="pulse"
        playHref={modePath('pulse', 'play/on-and-on')}
        playLabel="Play On & On"
      />
    </div>
  );
}
